/* eslint-disable react-hooks-ext/exhaustive-deps -- TODO(react-hooks): 1 hook(s) need dependency review; fix each and remove this directive */
import { forwardRef, useRef, useEffect, useImperativeHandle, useState, useCallback } from "react";
import { Box } from "@mui/material";

export interface SignatureComponentProps {
  id?: string;
  backgroundColor?: string;
  strokeColor?: string;
  minStrokeWidth?: number;
  maxStrokeWidth?: number;
  velocity?: number;
  disabled?: boolean;
  isReadOnly?: boolean;
  saveWithBackground?: boolean;
  cssClass?: string;
  className?: string;
  initialImageUrl?: string;
  // Callbacks - Syncfusion compatible
  change?: () => void;
  created?: () => void;
  beforeSave?: (args: { fileName: string; type: string }) => void;
  // Style props
  style?: React.CSSProperties;
  sx?: any;
}

export interface SignatureComponentRef {
  getSignature: (type?: string) => string;
  save: (type?: string, fileName?: string) => void;
  clear: () => void;
  isEmpty: () => boolean;
  canRedo: () => boolean;
  canUndo: () => boolean;
  redo: () => void;
  undo: () => void;
}

interface Point {
  x: number;
  y: number;
  time: number;
}

const SignatureComponent = forwardRef<SignatureComponentRef, SignatureComponentProps>((props, ref) => {
  const {
    id,
    backgroundColor = "transparent",
    strokeColor = "#000000",
    minStrokeWidth = 0.5,
    maxStrokeWidth = 2.5,
    velocity = 0.7,
    disabled = false,
    isReadOnly = false,
    saveWithBackground = true,
    cssClass,
    className,
    change,
    created,
    beforeSave,
    style,
    sx,
    initialImageUrl,
  } = props;

  const canvasRef = useRef<HTMLCanvasElement>(null);
  const contextRef = useRef<CanvasRenderingContext2D | null>(null);
  const [isDrawing, setIsDrawing] = useState(false);
  const pointsRef = useRef<Point[]>([]);
  const historyRef = useRef<ImageData[]>([]);
  const historyIndexRef = useRef(-1);
  const lastPointRef = useRef<Point | null>(null);
  const lastWidthRef = useRef(minStrokeWidth);

  const saveHistory = useCallback(() => {
    const canvas = canvasRef.current;
    const ctx = contextRef.current;
    if (!canvas || !ctx) return;

    const imageData = ctx.getImageData(0, 0, canvas.width, canvas.height);
    historyRef.current = historyRef.current.slice(0, historyIndexRef.current + 1);
    historyRef.current.push(imageData);
    historyIndexRef.current = historyRef.current.length - 1;
  }, []);

  useEffect(() => {
    if (created) {
      created();
    }
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    // Set canvas size based on parent
    const resizeCanvas = () => {
      const rect = canvas.getBoundingClientRect();
      const dpr = window.devicePixelRatio || 1;

      canvas.width = rect.width * dpr;
      canvas.height = rect.height * dpr;

      ctx.scale(dpr, dpr);
      ctx.lineCap = "round";
      ctx.lineJoin = "round";
      ctx.strokeStyle = strokeColor;

      // Clear with background
      ctx.fillStyle = backgroundColor;
      ctx.fillRect(0, 0, rect.width, rect.height);
    };

    resizeCanvas();
    contextRef.current = ctx;

    // Save initial state
    saveHistory();

    window.addEventListener("resize", resizeCanvas);
    return () => window.removeEventListener("resize", resizeCanvas);
  }, [backgroundColor, strokeColor, saveHistory]);

  useEffect(() => {
    if (!initialImageUrl) return;
    const canvas = canvasRef.current;
    if (!canvas) return;
    const img = new Image();
    img.crossOrigin = "anonymous";
    img.onload = () => {
      const ctx = contextRef.current;
      if (!ctx) return;
      ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
      saveHistory();
    };
    img.onerror = () => {};
    img.src = initialImageUrl;
  }, [initialImageUrl, saveHistory]);

  const getPoint = useCallback((e: React.MouseEvent | React.TouchEvent): Point => {
    const canvas = canvasRef.current!;
    const rect = canvas.getBoundingClientRect();

    let clientX, clientY;
    if ("touches" in e && e.touches.length > 0) {
      clientX = e.touches[0]!.clientX;
      clientY = e.touches[0]!.clientY;
    } else if ("clientX" in e) {
      clientX = e.clientX;
      clientY = e.clientY;
    } else {
      clientX = 0;
      clientY = 0;
    }

    return {
      x: clientX - rect.left,
      y: clientY - rect.top,
      time: Date.now(),
    };
  }, []);

  const getStrokeWidth = useCallback(
    (p1: Point, p2: Point): number => {
      const distance = Math.sqrt(Math.pow(p2.x - p1.x, 2) + Math.pow(p2.y - p1.y, 2));
      const timeDiff = Math.max(p2.time - p1.time, 1);
      const speed = distance / timeDiff;

      // Calculate width based on velocity
      const targetWidth = Math.max(maxStrokeWidth - speed * velocity, minStrokeWidth);

      // Smooth width transition
      const newWidth = lastWidthRef.current * 0.6 + targetWidth * 0.4;
      lastWidthRef.current = newWidth;

      return newWidth;
    },
    [minStrokeWidth, maxStrokeWidth, velocity],
  );

  const drawLine = useCallback(
    (p1: Point, p2: Point, width: number) => {
      const ctx = contextRef.current;
      if (!ctx) return;

      ctx.beginPath();
      ctx.moveTo(p1.x, p1.y);
      ctx.lineTo(p2.x, p2.y);
      ctx.strokeStyle = strokeColor;
      ctx.lineWidth = width;
      ctx.stroke();
    },
    [strokeColor],
  );

  const handleStart = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      if (disabled || isReadOnly) return;
      e.preventDefault();

      const point = getPoint(e);
      pointsRef.current = [point];
      lastPointRef.current = point;
      lastWidthRef.current = minStrokeWidth;
      setIsDrawing(true);
    },
    [disabled, isReadOnly, getPoint, minStrokeWidth],
  );

  const handleMove = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      if (!isDrawing || disabled || isReadOnly) return;
      e.preventDefault();

      const point = getPoint(e);
      const lastPoint = lastPointRef.current;

      if (lastPoint) {
        const width = getStrokeWidth(lastPoint, point);
        drawLine(lastPoint, point, width);
      }

      pointsRef.current.push(point);
      lastPointRef.current = point;
    },
    [isDrawing, disabled, isReadOnly, getPoint, getStrokeWidth, drawLine],
  );

  const handleEnd = useCallback(() => {
    if (!isDrawing) return;

    setIsDrawing(false);
    pointsRef.current = [];
    lastPointRef.current = null;
    saveHistory();

    if (change) {
      change();
    }
  }, [isDrawing, saveHistory, change]);

  useImperativeHandle(ref, () => ({
    getSignature: (type = "image/png") => {
      const canvas = canvasRef.current;
      if (!canvas) return "";

      if (saveWithBackground) {
        return canvas.toDataURL(type);
      }

      // Create a new canvas without background
      const tempCanvas = document.createElement("canvas");
      tempCanvas.width = canvas.width;
      tempCanvas.height = canvas.height;
      const tempCtx = tempCanvas.getContext("2d");
      if (!tempCtx) return "";

      tempCtx.drawImage(canvas, 0, 0);
      return tempCanvas.toDataURL(type);
    },

    save: (type = "image/png", fileName = "signature") => {
      if (beforeSave) {
        beforeSave({ fileName, type });
      }

      const canvas = canvasRef.current;
      if (!canvas) return;

      const link = document.createElement("a");
      link.download = `${fileName}.${type.split("/")[1]}`;
      link.href = canvas.toDataURL(type);
      link.click();
    },

    clear: () => {
      const canvas = canvasRef.current;
      const ctx = contextRef.current;
      if (!canvas || !ctx) return;

      const rect = canvas.getBoundingClientRect();
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      if (backgroundColor && backgroundColor !== "transparent") {
        ctx.fillStyle = backgroundColor;
        ctx.fillRect(0, 0, rect.width, rect.height);
      }
      saveHistory();
    },

    isEmpty: () => {
      return historyRef.current.length <= 1;
    },

    canUndo: () => {
      return historyIndexRef.current > 0;
    },

    canRedo: () => {
      return historyIndexRef.current < historyRef.current.length - 1;
    },

    undo: () => {
      if (historyIndexRef.current <= 0) return;

      historyIndexRef.current--;
      const ctx = contextRef.current;
      const imageData = historyRef.current[historyIndexRef.current];
      if (!ctx || !imageData) return;

      ctx.putImageData(imageData, 0, 0);
    },

    redo: () => {
      if (historyIndexRef.current >= historyRef.current.length - 1) return;

      historyIndexRef.current++;
      const ctx = contextRef.current;
      const imageData = historyRef.current[historyIndexRef.current];
      if (!ctx || !imageData) return;

      ctx.putImageData(imageData, 0, 0);
    },
  }));

  return (
    <Box
      id={id}
      className={className || cssClass}
      sx={{
        width: "100%",
        height: "12.5rem",
        position: "relative",
        touchAction: "none",
        ...sx,
      }}
      style={style}
    >
      <canvas
        ref={canvasRef}
        style={{
          width: "100%",
          height: "100%",
          cursor: disabled || isReadOnly ? "not-allowed" : "crosshair",
          backgroundColor,
        }}
        onMouseDown={handleStart}
        onMouseMove={handleMove}
        onMouseUp={handleEnd}
        onMouseLeave={handleEnd}
        onTouchStart={handleStart}
        onTouchMove={handleMove}
        onTouchEnd={handleEnd}
      />
    </Box>
  );
});

SignatureComponent.displayName = "SignatureComponent";

export default SignatureComponent;
